import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import HomeIcon from "@mui/icons-material/Home";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import SettingsApplicationsOutlinedIcon from "@mui/icons-material/SettingsApplicationsOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Divider } from "@mui/material";
import { MessagePayload, onMessage } from "firebase/messaging";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import AuthContext from "src/app/contexts/auth-context";
import { useAuth } from "src/app/features/auth/hooks/useAuth";
import { messaging } from "src/app/firebase/firebase";
import UserProfile from "src/app/pages/auth/components/UserProfile";
import DropDown from "src/app/pages/common/drop-down/drop-down";
import { DropdownItemSelectedParams } from "src/app/pages/common/drop-down/DropdownItemSelectedParams";
import { FCMNotificationTypeEnum } from "src/app/pages/common/types/notification-type-enum";
import { PageLoadingContext } from "src/app/routing/AppRoutes";
import {
  useGetLanguagesQuery,
  useGetPaymentIdByPaymentNumberLazyQuery,
  useGetUserNotificationsLazyQuery,
  useMarkNotificationAsReadMutation,
  useUpdateUserDefaultLocationMutation,
  useUpsertUserDefaultLanguagemutationMutation,
} from "src/generated/graphql";
import Notifier, {
  emptyNotification,
  NotificationMessage,
  NotificationType,
} from "../../../common/notifier/notifier";
import { ReactComponent as Karibu } from "../images/Karibu.svg";
import Notification from "./components/Notification";
import { NotificationCardProps } from "./components/NotificationCard";
import style from "./masterHeader.module.scss";
import { formatApolloError } from "src/app/graphql/graphql-helpers";
import {
  LocationCityOutlined,
  LocationOnOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import { usePermission } from "src/app/hooks/permissions/use-permission";
import { UserPermissions } from "src/app/providers/permissions/permissions";
interface FCMNotificationData {
  notification_id: string;
  component_name: string;
  component_id: string;
  notification_type_id: string;
}

interface LocationType {
  id: number;
  name: string;
  companyId: string;
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}
export const MasterHeader = () => {
  const { t, i18n } = useTranslation();
  const normalizeLanguageCode = (value?: string | null): "en" | "fr" | "pt" => {
    const normalized = value?.trim().toLowerCase() ?? "";
    if (normalized.startsWith("fr")) return "fr";
    if (normalized.startsWith("pt")) return "pt";
    return "en";
  };
  const userService = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnUtilityPage = location.pathname.startsWith("/karibu-utility");
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] =
    useState<NotificationMessage>();
  const [profileIsOpen, setProfileIsOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<LocationType[]>();
  const [hoverPanelPosition, setHoverPanelPosition] = useState({
    top: 0,
    left: 0,
  });

  const pageLoadingContext = useContext(PageLoadingContext);

  const [
    updateUserDefaultLocation,
    {
      data: updateUserDefaultLocationData,
      loading: updatingUserDefaultLocation,
      error: updateUserDefaultLocationError,
    },
  ] = useUpdateUserDefaultLocationMutation();

  const locationSelected = useCallback(
    (params: DropdownItemSelectedParams) => {
      let selectedUserLocation =
        userService?.authContextData?.currentUser?.locations?.at(
          params.selectedIndex,
        );
      if (
        selectedUserLocation?.id !==
          userService?.authContextData?.currentUser?.locationId &&
        params.selectionProcess === "Manual"
      ) {
        updateUserDefaultLocation({
          variables: {
            locationId: Number(selectedUserLocation?.id),
          },
        });
      }
    },
    [userService?.authContextData?.currentUser, updateUserDefaultLocation],
  );

  const [getPaymentId, { data: paymentData, loading: gettingPaymentId }] =
    useGetPaymentIdByPaymentNumberLazyQuery();

  const [getNotifications, { data: notificationData }] =
    useGetUserNotificationsLazyQuery();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [fcmPayloadData, setFCMPayloadData] = useState<MessagePayload>();
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<
    DropdownItemSelectedParams | undefined
  >();
  const { data: languagesData, loading: loadingDefaultLanguage } =
    useGetLanguagesQuery();

  const [initialSelectedLanguageIndex, setInitialSelectedLanguageIndex] =
    useState<number>();

  const [isOpen, setIsOpen] = useState(false);
  const [openNav, setOpenNav] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const mobileNavButtonRef = useRef<HTMLButtonElement>(null);

  // Touch devices have no hover, so close the mobile nav when tapping/clicking
  // outside of it (and outside its toggle button).
  useEffect(() => {
    if (!openNav) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        mobileNavRef.current?.contains(target) ||
        mobileNavButtonRef.current?.contains(target)
      ) {
        return;
      }
      setOpenNav(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [openNav]);

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (
      updateUserDefaultLocationData?.updateUserDefaultLocation?.user?.locationId
    ) {
      navigate("/landing");
      window.location.reload();
    }
  }, [updateUserDefaultLocationData, navigate]);

  useEffect(() => {
    if (updateUserDefaultLocationError && updatingUserDefaultLocation) {
      setNotificationMessage({
        message: formatApolloError(updateUserDefaultLocationError),
        type: NotificationType.error,
      });
    }
  }, [updatingUserDefaultLocation, updateUserDefaultLocationError]);

  useEffect(() => {
    if (selectedDropdownValue?.selectionProcess == "Manual") {
      const selectedValue = selectedDropdownValue?.selectedItem?.toLowerCase();
      const languageCode =
        selectedDropdownValue?.selectedItem.length < 3
          ? selectedValue
          : languagesData?.languages.find(
              (lang) => lang?.lang.toLocaleLowerCase() == selectedValue,
            )?.code;

      i18n.changeLanguage(normalizeLanguageCode(languageCode));
    }
  }, [userService, i18n, selectedDropdownValue, languagesData?.languages]);

  useEffect(() => {
    if (userService?.authContextData?.currentUser?.language?.code) {
      i18n.changeLanguage(
        normalizeLanguageCode(
          userService?.authContextData?.currentUser?.language?.code,
        ),
      );
    }
  }, [i18n, userService?.authContextData?.currentUser?.language?.code]);

  useEffect(() => {
    if (
      userService?.authContextData?.currentUser?.language?.code &&
      (languagesData?.languages?.length ?? 0) > 0
    ) {
      for (let i = 0; i < (languagesData?.languages?.length ?? 0); i++) {
        const _language = languagesData?.languages[i];
        if (
          _language?.code ==
          userService?.authContextData?.currentUser?.language?.code
        ) {
          setInitialSelectedLanguageIndex(undefined);
          setTimeout(() => {
            setInitialSelectedLanguageIndex(i);
          }, 100);
          break;
        }
      }
    }
  }, [
    setInitialSelectedLanguageIndex,
    userService?.authContextData?.currentUser?.language?.code,
    languagesData?.languages,
  ]);

  const gotoDashboard = () => {
    navigate("/landing");
  };

  const onHideNotification = () => {
    setNotificationMessage(emptyNotification(notificationMessage));
    getNotifications();
  };
  const [notifications, setNotifications] = useState<NotificationCardProps[]>(
    [],
  );
  const [isFcmNotification, setIsFcmNotification] = useState<boolean>();

  useEffect(() => {
    if (notificationData?.userNotifications?.nodes?.length) {
      let noticeData = notificationData?.userNotifications?.nodes?.map(
        (val) => {
          return {
            title: val?.notification?.title,
            message: val?.notification?.body,
            date: val?.createdAt,
            onClick: () => {
              setIsFcmNotification(false);
              if (
                val?.notification?.notificationTypeId ===
                Number(FCMNotificationTypeEnum.ManualPaymentRequest)
              ) {
                getPaymentId({
                  variables: {
                    paymentNumber: String(val?.notification?.componentId),
                  },
                });
              }
              if (
                val?.notification?.notificationTypeId ===
                Number(FCMNotificationTypeEnum.Quotation)
              ) {
                navigate("orders-details/" + val?.notification?.componentId);
              }
              markNotificationAsRead({
                variables: {
                  notificationId: Number(val?.notification?.id),
                },
              });
              getNotifications();
            },
          };
        },
      ) as NotificationCardProps[];
      setNotifications(noticeData);
    }
  }, [
    notificationData?.userNotifications?.nodes,
    markNotificationAsRead,
    getPaymentId,
    getNotifications,
    navigate,
  ]);

  useEffect(() => {
    if (!!gettingPaymentId) {
      pageLoadingContext.setPageLoading?.(true);
    }
    if (paymentData?.payments?.nodes?.at(0)?.id) {
      pageLoadingContext.setPageLoading?.(false);
    }
  }, [gettingPaymentId, pageLoadingContext, paymentData?.payments?.nodes]);

  const [upsertUserDefaultLanguagemutationMutation] =
    useUpsertUserDefaultLanguagemutationMutation();

  const onDropdownItemSelected = (
    itemSelectedParams: DropdownItemSelectedParams,
  ) => {
    if (itemSelectedParams.selectionProcess == "Automatic") return;
    setSelectedDropdownValue(itemSelectedParams);
    let selectedLang;
    if (itemSelectedParams.selectedItem.length < 3)
      selectedLang = languagesData?.languages?.find(
        (val) => val.code === itemSelectedParams.selectedItem?.toLowerCase(),
      );
    else
      selectedLang = languagesData?.languages?.find(
        (val) =>
          val.lang.toLowerCase() ===
          itemSelectedParams.selectedItem?.toLowerCase(),
      );

    if (selectedLang && selectedLang.id !== undefined) {
      upsertUserDefaultLanguagemutationMutation({
        variables: {
          langId: { languageId: selectedLang.id },
        },
      });
    }
  };

  const FilterItems = useMemo<string[]>(() => {
    if (languagesData?.languages) {
      return languagesData?.languages?.map((value) =>
        value?.code?.toUpperCase(),
      );
    }
    return [];
  }, [languagesData?.languages]);

  const FilterMobileItems = useMemo<string[]>(() => {
    if (languagesData?.languages) {
      return languagesData?.languages?.map((value) =>
        value?.lang?.toUpperCase(),
      );
    }
    return [];
  }, [languagesData?.languages]);

  const { logout } = useAuth();

  onMessage(messaging, (payload) => {
    setIsFcmNotification(true);
    if (
      payload?.data?.notification_type_id ===
      FCMNotificationTypeEnum.ManualPaymentRequest
    ) {
      getPaymentId({
        variables: {
          paymentNumber: payload?.data?.component_id,
        },
      });
    }
    setFCMPayloadData(payload);
  });

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  useEffect(() => {
    if (width > 769) setOpenNav(false);
  }, [width]);

  useEffect(() => {
    if (paymentData?.payments?.nodes?.at(0)?.id && isFcmNotification) {
      setNotificationMessage({
        message: String(fcmPayloadData?.notification?.title),
        type: NotificationType.info,
      });
    }
    if (
      paymentData?.payments?.nodes?.at(0)?.id &&
      isFcmNotification === false
    ) {
      navigate(`manual-payments/${paymentData?.payments?.nodes?.at(0)?.id}`);
      setIsFcmNotification(undefined);
      getNotifications();
    }
  }, [
    paymentData?.payments?.nodes,
    fcmPayloadData?.notification?.title,
    navigate,
    getNotifications,
    isFcmNotification,
  ]);

  const handleNotificationPress = (data: FCMNotificationData) => {
    if (
      data.notification_type_id === FCMNotificationTypeEnum.ManualPaymentRequest
    ) {
      navigate(`manual-payments/${paymentData?.payments?.nodes?.at(0)?.id}`);
      markNotificationAsRead({
        variables: {
          notificationId: Number(data.notification_id),
        },
      });
      setIsFcmNotification(undefined);
    }
    if (data.notification_type_id === FCMNotificationTypeEnum.Quotation) {
      navigate("orders-details/" + data.component_id);
      markNotificationAsRead({
        variables: {
          notificationId: Number(data.notification_id),
        },
      });
      setIsFcmNotification(undefined);
    }
    getNotifications();
  };

  useEffect(() => {
    if (width <= 1023) setProfileIsOpen(false);
  }, [width]);

  const assignedLocations =
    userService?.authContextData?.currentUser?.locations ?? [];

  const {
    hasPermission,
    hasOnlyKaribuUtilityAccess,
    hasKaribuUtilityAccess,
    primaryRoleLabel,
  } = usePermission();

  const hasConfigAccess = hasPermission([
    UserPermissions.USERS.VIEW,
    UserPermissions.LOCATIONS.VIEW,
    UserPermissions.BANKS.VIEW,
    UserPermissions.SETTLEMENT_BANKS.VIEW,
    UserPermissions.PRODUCTS.VIEW,
    UserPermissions.DELIVERY_TRACKING.VIEW,
    UserPermissions.TRUCK_TRACKING.VIEW,
    UserPermissions.CUSTOMER_MANAGEMENT.VIEW,
  ]);

  const hasPaymentsPermission = hasPermission([
    UserPermissions.PAYMENT_TRANSCRIPTION.VIEW,
    UserPermissions.TREASURER_APPROVAL.VIEW,
    UserPermissions.FINANCE_OFFICER_APPROVAL.VIEW,
    UserPermissions.PAYMENTS.VIEW,
  ]);

  const hasBankGuaranteePermissions = hasPermission([
    UserPermissions.PAYMENT_TRANSCRIPTION.VIEW,
    UserPermissions.TREASURER_APPROVAL.VIEW,
    UserPermissions.FINANCE_OFFICER_APPROVAL.VIEW,
  ]);

  const currentUser = userService?.authContextData?.currentUser;
  const canRenderLogisticsNavigation =
    hasPermission([
      UserPermissions.ORDERS.VIEW,
      UserPermissions.LOGISTICS.VIEW,
    ]) &&
    Boolean(currentUser?.location?.useDeliveredOrderManagement);
  const canRenderBankGuaranteeNavigation =
    hasBankGuaranteePermissions &&
    Boolean(currentUser?.location?.allowBankGuarantee);

  // If user has only Karibu Utility access, show simplified navigation
  const gotoKaribuUtility = () => {
    navigate("/karibu-utility");
  };

  // For users with both main app and utility access
  const hasMixedAccess = hasKaribuUtilityAccess && !hasOnlyKaribuUtilityAccess;

  return (
    <>
      <div className="lg:hidden">
        <div className="bg-white flex justify-between items-center w-full relative">
          <span
            onClick={
              hasOnlyKaribuUtilityAccess
                ? gotoKaribuUtility
                : isOnUtilityPage
                  ? gotoKaribuUtility
                  : gotoDashboard
            }
            className=" cursor-pointer p-4 "
          >
            <Karibu
              width={width * 0.15}
              height={Number(width * 0.04).toFixed(2)}
            />
          </span>

          <div className="flex gap-2 mr-5">
            {hasMixedAccess && (
              <div className="relative">
                <button
                  className={`relative z-5 block border-2 rounded-full ${
                    isOnUtilityPage ? "py-1 px-2" : "p-2"
                  }`}
                  onClick={() =>
                    isOnUtilityPage
                      ? navigate("/landing")
                      : navigate("/karibu-utility/quotation-bod")
                  }
                  title={
                    isOnUtilityPage
                      ? t("MainHeader.back-to-main")
                      : t("MainHeader.go-to-utility")
                  }
                >
                  <SwapHorizOutlined sx={{ fontSize: isOnUtilityPage ? 14 : 20 }} />
                </button>
              </div>
            )}
            <div className="relative">
              <button
                ref={mobileNavButtonRef}
                data-testid="mobile-nav-toggle"
                className={`relative z-5 block border-2 rounded-full ${
                  isOnUtilityPage ? "py-1 px-2" : "p-2"
                }`}
                onClick={() => setOpenNav(!openNav)}
              >
                <MenuIcon sx={{ fontSize: isOnUtilityPage ? 14 : 20 }} />
              </button>
            </div>
            {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsOpen(false);
                    }, 400);
                  }}
                  disabled={!notificationData?.userNotifications?.nodes?.length}
                  className="relative z-5 block border-2 rounded-full p-2"
                >
                  <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
                  {!!notificationData?.userNotifications?.nodes?.length && (
                    <span className="absolute top-0 -right-2 inline-block px-2 py-1 text-xs font-bold leading-none text-white bg-yardBackgroundLight rounded-full">
                      {notificationData?.userNotifications?.nodes?.length}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="absolute right-0 w-[24vw] mt-2 bg-white rounded-lg border shadow-xl z-[12]">
                    <Notification notificationData={notifications} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <Notifier
          notificationMessage={notificationMessage}
          onHideNotifier={onHideNotification}
          onClick={() => {
            handleNotificationPress(
              fcmPayloadData?.data as any as FCMNotificationData,
            );
            onHideNotification();
          }}
        />
        {openNav && (
          <div
            ref={mobileNavRef}
            data-testid="mobile-nav"
            className="absolute bg-[#716e6e7e] w-[45vw] min-h-fit max-h-[70%] overflow-y-auto z-[22] top-[6%] right-8 "
          >
            <div
              className="relative h-full bg-white border shadow-xl p-6 flex flex-col"
              onMouseLeave={() => {
                setTimeout(() => {
                  setOpenNav(false);
                }, 400);
              }}
            >
              <div className="rounded-full w-[80px] h-[80px] bg-[#009d936b] p-8 mb-6 mt-8 ml-4 items-center ">
                <p className="text-[#009D93] font-semibold	text-5xl text-center">
                  {userService?.authContextData?.currentUser?.fullName
                    .split(" (")[0]
                    .split(", ")
                    .map((n) => n[0])
                    .reverse()
                    .join("")}
                </p>
              </div>
              <div className=" ml-4 mb-4">
                <div>{userService?.authContextData?.currentUser?.fullName}</div>
                <div className="text-slate-400 text-sm my-1">
                  {userService?.authContextData?.currentUser?.email}
                </div>
                <div className="text-blue-800 font-semibold text-lg">
                  {primaryRoleLabel}
                </div>
              </div>
              <Divider />
              <div className=" mt-3 ">
                {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
                  <div className="flex items-center self-end mb-[7px]">
                    {userService?.authContextData?.currentUser?.companies
                      .length === 1 ? (
                      <>
                        {userService?.authContextData?.currentUser?.locations
                          .length === 1 ? (
                          <>
                            <div className="relative mr-1 xl:mr-12 flex items-center justify-center  w-[10rem] xl:w-[18rem]">
                              <LocationCityOutlined fontSize="large" />
                              <h2
                                className={`${" ml-2 text-[15px] text-[#274867] w-[120px] text-nowrap text-ellipsis overflow-hidden"} truncate overflow-hidden whitespace-nowrap`}
                              >
                                {userService?.authContextData?.currentUser?.locations?.map(
                                  (i) => i?.name,
                                ) ?? ""}
                              </h2>
                            </div>
                          </>
                        ) : (
                          <div className="masterHeader__right flex items-center z-[100] select-none space-x-3">
                            <div className="w-[15rem] border mr-5 rounded-lg">
                              <DropDown
                                frontLogoType="location"
                                size="lg"
                                initialSelectedIndex={userService?.authContextData?.currentUser?.locations?.findIndex(
                                  (i) =>
                                    i.id ===
                                    userService?.authContextData?.currentUser
                                      ?.locationId,
                                )}
                                searchList={
                                  userService?.authContextData?.currentUser?.locations?.map(
                                    (i) => i?.name,
                                  ) ?? []
                                }
                                onItemSelected={locationSelected}
                                className="w-[90%]"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="masterHeader__right flex items-center z-[100] select-none space-x-3">
                          <div className=" w-[70%] xl:w-[18rem] border mr-5 rounded-lg">
                            <DropDown
                              frontLogoType="city"
                              size="lg"
                              initialSelectedIndex={userService?.authContextData?.currentUser?.companies?.findIndex(
                                (i) =>
                                  i.id ===
                                  userService?.authContextData?.currentUser
                                    ?.companyId,
                              )}
                              searchList={
                                userService?.authContextData?.currentUser?.companies?.map(
                                  (i) => i?.name,
                                ) ?? []
                              }
                              onItemSelected={locationSelected}
                              className="w-[90%]"
                              onItemHover={(companyId, event) => {
                                const companyLocations =
                                  assignedLocations.filter(
                                    (location) =>
                                      String(location.companyId) ===
                                      String(companyId),
                                  );
                                setFilteredLocations(
                                  companyLocations.map((location) => ({
                                    ...location,
                                    companyId: String(location.companyId),
                                  })),
                                );
                                const hoveredRect = (
                                  event.target as HTMLElement
                                ).getBoundingClientRect();

                                const panelWidth = 100;
                                const padding = 2;

                                const spaceToRight =
                                  window.innerWidth - hoveredRect.right;
                                const renderToLeft =
                                  spaceToRight < panelWidth + padding;

                                setHoverPanelPosition({
                                  top: hoveredRect.top + window.scrollY,
                                  left: renderToLeft
                                    ? hoveredRect.left - panelWidth - padding
                                    : hoveredRect.right + padding,
                                });
                              }}
                              itemIdResolver={(itemName, index) => {
                                const id =
                                  userService?.authContextData?.currentUser
                                    ?.companies?.[index]?.id;
                                return id !== undefined && id !== null
                                  ? String(id)
                                  : "";
                              }}
                            />
                          </div>
                          {(filteredLocations ?? []).length > 0 && (
                            <div
                              style={{
                                top: hoverPanelPosition.top,
                                left: hoverPanelPosition.left,
                                position: "absolute",
                                zIndex: 1000,
                              }}
                              className={`
                          "m-0 w-[12.3rem] border rounded-lg bg-white shadow-md overflow-y-auto",
                          ${
                            (filteredLocations?.length ?? 0) > 1
                              ? "max-h-[12rem]"
                              : "h-[3.5rem]"
                          }
                        `}
                              onMouseLeave={() => {
                                setFilteredLocations([]);
                              }}
                            >
                              <ul className="list-none p-2 space-y-2">
                                {(filteredLocations ?? []).map((loc) => (
                                  <li
                                    key={loc.id}
                                    className="flex items-center justify-start space-x-2 px-2 py-2 rounded-md text-black text-base cursor-pointer 
                              hover:bg-yardBlue hover:text-white active:bg-yardBlue active:text-white transition-colors duration-200"
                                    onClick={() => {
                                      const userLocations =
                                        userService?.authContextData
                                          ?.currentUser?.locations ?? [];
                                      const index = userLocations.findIndex(
                                        (location) => location.id === loc.id,
                                      );
                                      locationSelected({
                                        selectedIndex: index,
                                        selectedItem: loc.name,
                                        selectionProcess: "Manual",
                                      });
                                    }}
                                  >
                                    <LocationOnOutlined fontSize="small" />
                                    <span className="text-[13px>">
                                      {loc.name}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <Divider />
                <div className=" ">
                  {!!(
                    languagesData &&
                    !isNaN(Number(initialSelectedLanguageIndex))
                  ) && (
                    <DropDown
                      className={""}
                      onItemSelected={onDropdownItemSelected}
                      searchList={FilterMobileItems}
                      initialSelectedIndex={initialSelectedLanguageIndex}
                      size="lg"
                      frontLogoType="language"
                    />
                  )}
                </div>
                <Divider />
                <p className="mt-4 ml-4 text-[13px] mb-1  text-slate-400">
                  {t("MainHeader.menu")}
                </p>
                <ul
                  className={`flex ${style.mainNavigation} ${style.ul} flex-col mb-2 z-0`}
                >
                  {/* Hide navigation for users with only Karibu Utility access */}
                  {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
                    <>
                      <li className="relative p-2">
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.activealt : ""}`
                          }
                          end
                          to="/landing"
                        >
                          <p className="flex items-center gap-3 text-xl">
                            <HomeIcon />
                            <>{t("MainHeader.overview")}</>
                          </p>
                        </NavLink>
                      </li>
                      {hasPermission(UserPermissions.ORDERS.VIEW) && (
                        <li className="relative p-2">
                          <NavLink
                            className={({ isActive }) =>
                              `${isActive ? style.activealt : ""}`
                            }
                            to="/orders"
                          >
                            <p className="flex items-center gap-3 text-xl">
                              <ShoppingCartOutlinedIcon />
                              <>{t("MainHeader.orders")}</>
                            </p>
                          </NavLink>
                        </li>
                      )}
                      {hasPaymentsPermission && (
                        <li className="relative p-2">
                          <NavLink
                            className={({ isActive }) =>
                              `${isActive ? style.activealt : ""}`
                            }
                            to="/payments"
                          >
                            <p className="flex items-center gap-3 text-xl">
                              <CreditCardOutlinedIcon />
                              <>{t("MainHeader.payments")}</>
                            </p>
                          </NavLink>
                        </li>
                      )}
                      {hasConfigAccess && (
                        <li className="relative p-2">
                          <NavLink
                            className={({ isActive }) =>
                              `${isActive ? style.activealt : ""}`
                            }
                            to="/configurations"
                          >
                            <p className="flex items-center gap-3 text-xl">
                              <ListAltIcon />
                              <>{t("MainHeader.configuration")}</>
                            </p>
                          </NavLink>
                        </li>
                      )}
                      {canRenderBankGuaranteeNavigation && (
                        <li>
                          <NavLink
                            className={({ isActive }) =>
                              `${isActive ? style.active : ""}`
                            }
                            to="/bank-guarantee"
                          >
                            <p className="flex items-center gap-3 text-xl">
                              <CreditCardOutlinedIcon />
                              {t("MainHeader.bank-guarantee")}
                            </p>
                          </NavLink>
                        </li>
                      )}
                      <li className="relative p-2">
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.activealt : ""}`
                          }
                          to="/reports"
                        >
                          <p className="flex items-center gap-3 text-xl">
                            <SettingsApplicationsOutlinedIcon />
                            <>{t("MainHeader.reports")}</>
                          </p>
                        </NavLink>
                      </li>
                      {hasPermission(UserPermissions.PRODUCTS.VIEW) && (
                        <li className="relative p-2">
                          <NavLink
                            className={({ isActive }) =>
                              `${isActive ? style.activealt : ""}`
                            }
                            to="/content-management"
                          >
                            <p className="flex items-center gap-3 text-xl">
                              <AdminPanelSettingsOutlinedIcon />
                              <>{t("MainHeader.content-management")}</>
                            </p>
                          </NavLink>
                        </li>
                      )}
                    </>
                  )}
                  {(hasOnlyKaribuUtilityAccess ||
                    (hasMixedAccess && isOnUtilityPage)) && (
                    <div className="masterHeader__middle ml-4 min-w-0 z-0">
                      <ul
                        className={`${style.mainNavigation} ${style.ul} z-1 text-sm xl:text-base 2xl:text-lg flex flex-col`}
                      >
                        <>
                          <li>
                            <NavLink
                              className={({ isActive }) =>
                                `${isActive ? style.active : ""}`
                              }
                              to="/karibu-utility/quotation-bod"
                            >
                              {t("karibu-utility.tabs.quotation-bod")}
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className={({ isActive }) =>
                                `${isActive ? style.active : ""}`
                              }
                              to="/karibu-utility/sales-order-bod"
                            >
                              {t("karibu-utility.tabs.sales-order-bod")}
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className={({ isActive }) =>
                                `${isActive ? style.active : ""}`
                              }
                              to="/karibu-utility/payment-bod"
                            >
                              {t("karibu-utility.tabs.payment-bod")}
                            </NavLink>
                          </li>
                        </>
                      </ul>
                    </div>
                  )}
                </ul>
                <Divider />
                {hasMixedAccess && (
                  <>
                    <button
                      onClick={() => {
                        setOpenNav(false);
                        isOnUtilityPage
                          ? navigate("/landing")
                          : navigate("/karibu-utility/quotation-bod");
                      }}
                      className="text-[#274867] mt-4 ml-4 text-left flex gap-4 text-xl items-center"
                    >
                      <SwapHorizOutlined />
                      <p>
                        {isOnUtilityPage
                          ? t("MainHeader.back-to-main")
                          : t("MainHeader.go-to-utility")}
                      </p>
                    </button>
                    <Divider className="mt-4" />
                  </>
                )}
                <button
                  onClick={() => logout()}
                  className=" text-red-500 mt-4 ml-4 text-left flex gap-4 text-xl items-center"
                >
                  <ExitToAppOutlinedIcon />
                  <p className=""> {t("modules.logout.logout")}</p>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className=" hidden lg:block">
        <div
          className={`bg-white flex py-4 xl:py-0 px-6 items-center justify-between `}
        >
          <div className="flex items-center">
            <span
              onClick={
                hasOnlyKaribuUtilityAccess
                  ? gotoKaribuUtility
                  : isOnUtilityPage
                    ? gotoKaribuUtility
                    : gotoDashboard
              }
              style={{ cursor: "pointer" }}
            >
              <Karibu width={width * 0.06} height={width * 0.03} />
            </span>
            {/* Hide navigation for users with only Karibu Utility access */}
            {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
              <div className="masterHeader__middle ml-4 flex-1 min-w-0 z-0">
                <ul
                  className={`${style.mainNavigation} ${style.ul} z-1 text-sm xl:text-base 2xl:text-lg flex items-center`}
                >
                  <>
                    <li>
                      <NavLink
                        className={({ isActive }) =>
                          `${isActive && style.active}
                             max-w-[148px] text-nowrap text-ellipsis overflow-hidden`
                        }
                        end
                        to="/landing"
                      >
                        {t("MainHeader.overview")}
                      </NavLink>
                    </li>
                    {hasPermission(UserPermissions.ORDERS.VIEW) && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.active : ""}`
                          }
                          to="/orders"
                        >
                          {t("MainHeader.orders")}
                        </NavLink>
                      </li>
                    )}
                    {hasPaymentsPermission && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.active : ""}`
                          }
                          to="/payments"
                        >
                          {t("MainHeader.payments")}
                        </NavLink>
                      </li>
                    )}

                    <li>
                      <NavLink
                        className={({ isActive }) =>
                          `${isActive ? style.active : ""}`
                        }
                        to="/reports"
                      >
                        {t("MainHeader.reports")}
                      </NavLink>
                    </li>
                    {canRenderBankGuaranteeNavigation && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.active : ""}`
                          }
                          to="/bank-guarantee"
                        >
                          {t("modules.labels.bank-guarantee")}
                        </NavLink>
                      </li>
                    )}
                    {hasConfigAccess && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.active : ""}`
                          }
                          to="/configurations"
                        >
                          {t("MainHeader.configuration")}
                        </NavLink>
                      </li>
                    )}
                    {hasPermission(UserPermissions.PRODUCTS.VIEW) && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${
                              isActive
                                ? `${
                                    width < 1100 || width >= 1440
                                      ? `${style.active}`
                                      : `${style.active} max-w-[146px] text-nowrap text-ellipsis overflow-hidden`
                                  }`
                                : `${
                                    width < 1100 || width >= 1440
                                      ? ""
                                      : "max-w-[140px] text-nowrap text-ellipsis overflow-hidden"
                                  }`
                            }`
                          }
                          to="/content-management"
                        >
                          {t("MainHeader.content-management")}
                        </NavLink>
                      </li>
                    )}
                    {canRenderLogisticsNavigation && (
                      <li>
                        <NavLink
                          className={({ isActive }) =>
                            `${isActive ? style.active : ""}`
                          }
                          to="/logistics"
                        >
                          {t("MainHeader.logistics")}
                        </NavLink>
                      </li>
                    )}
                  </>
                </ul>
              </div>
            )}
            {(hasOnlyKaribuUtilityAccess ||
              (hasMixedAccess && isOnUtilityPage)) && (
              <div className="masterHeader__middle ml-4 flex-1 min-w-0 z-0">
                <ul
                  className={`${style.mainNavigation} ${style.ul} z-1 text-sm xl:text-base 2xl:text-lg flex items-center`}
                >
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `${isActive ? style.active : ""}`
                      }
                      to="/karibu-utility/quotation-bod"
                    >
                      {t("karibu-utility.tabs.quotation-bod")}
                    </NavLink>
                  </li>
                  <>
                    <li>
                      <NavLink
                        className={({ isActive }) =>
                          `${isActive ? style.active : ""}`
                        }
                        to="/karibu-utility/sales-order-bod"
                      >
                        {t("karibu-utility.tabs.sales-order-bod")}
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        className={({ isActive }) =>
                          `${isActive ? style.active : ""}`
                        }
                        to="/karibu-utility/payment-bod"
                      >
                        {t("karibu-utility.tabs.payment-bod")}
                      </NavLink>
                    </li>
                  </>
                </ul>
              </div>
            )}
          </div>
          <div className="flex items-center  h-full ">
            {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
              <>
                {userService?.authContextData?.currentUser?.companies
                  ?.length === 1 ? (
                  <>
                    {userService?.authContextData?.currentUser?.locations
                      ?.length === 1 ? (
                      <>
                        <div className="relative mr-1 xl:mr-12 flex items-center justify-center  w-[10rem] xl:w-[18rem]">
                          <LocationCityOutlined fontSize="large" />
                          <h2
                            className={`${" ml-2 text-[15px] text-[#274867] w-[120px] text-nowrap text-ellipsis overflow-hidden"} truncate overflow-hidden whitespace-nowrap`}
                          >
                            {userService?.authContextData?.currentUser?.locations?.map(
                              (i) => i?.name,
                            ) ?? ""}
                          </h2>
                        </div>
                      </>
                    ) : (
                      <div className="masterHeader__right flex items-center z-[100] select-none space-x-3">
                        <div className="w-[15rem] border mr-5 rounded-lg">
                          <DropDown
                            frontLogoType="location"
                            size="lg"
                            initialSelectedIndex={userService?.authContextData?.currentUser?.locations?.findIndex(
                              (i) =>
                                i.id ===
                                userService?.authContextData?.currentUser
                                  ?.locationId,
                            )}
                            searchList={
                              userService?.authContextData?.currentUser?.locations?.map(
                                (i) => i?.name,
                              ) ?? []
                            }
                            onItemSelected={locationSelected}
                            className="w-[90%]"
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="masterHeader__right flex items-center z-[100] select-none space-x-3">
                      <div className=" w-[15rem] xl:w-[18rem] border mr-5 rounded-lg">
                        <DropDown
                          frontLogoType="city"
                          size="lg"
                          initialSelectedIndex={userService?.authContextData?.currentUser?.companies?.findIndex(
                            (i) =>
                              i.id ===
                              userService?.authContextData?.currentUser
                                ?.companyId,
                          )}
                          searchList={
                            userService?.authContextData?.currentUser?.companies?.map(
                              (i) => i?.name,
                            ) ?? []
                          }
                          onItemSelected={locationSelected}
                          className="w-[90%]"
                          onItemHover={(companyId, event) => {
                            const companyLocations = assignedLocations.filter(
                              (location) =>
                                String(location.companyId) ===
                                String(companyId),
                            );
                            setFilteredLocations(
                              companyLocations.map((location) => ({
                                ...location,
                                companyId: String(location.companyId),
                              })),
                            );
                            const hoveredRect = (
                              event.target as HTMLElement
                            ).getBoundingClientRect();

                            const panelWidth = 150;
                            const padding = 2;

                            const spaceToRight =
                              window.innerWidth - hoveredRect.right;
                            const renderToLeft =
                              spaceToRight < panelWidth + padding;

                            setHoverPanelPosition({
                              top: hoveredRect.top + window.scrollY,
                              left: renderToLeft
                                ? hoveredRect.left - panelWidth - padding
                                : hoveredRect.right + padding,
                            });
                          }}
                          itemIdResolver={(itemName, index) => {
                            const id =
                              userService?.authContextData?.currentUser
                                ?.companies?.[index]?.id;
                            return id !== undefined && id !== null
                              ? String(id)
                              : "";
                          }}
                        />
                      </div>
                      {(filteredLocations ?? [])?.length > 0 && (
                        <div
                          style={{
                            top: hoverPanelPosition.top,
                            left: hoverPanelPosition.left,
                            position: "absolute",
                            zIndex: 1000,
                          }}
                          className={`
                          "m-0 w-[12.3rem] border rounded-lg bg-white shadow-md overflow-y-auto",
                          ${
                            (filteredLocations?.length ?? 0) > 1
                              ? "max-h-[12rem]"
                              : "h-[3.5rem]"
                          }
                        `}
                          onMouseLeave={() => {
                            setFilteredLocations([]);
                          }}
                        >
                          <ul className="list-none p-2 space-y-2">
                            {(filteredLocations ?? []).map((loc) => (
                              <li
                                key={loc.id}
                                className="flex items-center justify-start space-x-2 px-2 py-2 rounded-md text-black text-base cursor-pointer 
                              hover:bg-yardBlue hover:text-white active:bg-yardBlue active:text-white transition-colors duration-200"
                                onClick={() => {
                                  const userLocations =
                                    userService?.authContextData?.currentUser
                                      ?.locations ?? [];
                                  const index = userLocations.findIndex(
                                    (location) => location.id === loc.id,
                                  );
                                  locationSelected({
                                    selectedIndex: index,
                                    selectedItem: loc.name,
                                    selectionProcess: "Manual",
                                  });
                                }}
                              >
                                <LocationOnOutlined fontSize="small" />
                                <span className="text-[13px]">{loc.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {hasMixedAccess && (
              <button
                className="relative z-5 block border-2 rounded-full p-2 mr-2"
                onClick={() =>
                  isOnUtilityPage
                    ? navigate("/landing")
                    : navigate("/karibu-utility/quotation-bod")
                }
                title={
                  isOnUtilityPage
                    ? t("MainHeader.back-to-main")
                    : t("MainHeader.go-to-utility")
                }
              >
                <SwapHorizOutlined sx={{ fontSize: 20 }} />
              </button>
            )}
            {!hasOnlyKaribuUtilityAccess && !isOnUtilityPage && (
              <div className="relative">
                <button
                  onClick={() => setNotificationIsOpen(!notificationIsOpen)}
                  onBlur={() => {
                    setTimeout(() => {
                      setNotificationIsOpen(false);
                    }, 400);
                  }}
                  disabled={!notificationData?.userNotifications?.nodes?.length}
                  className="relative z-5 block border-2 rounded-full p-2"
                >
                  <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
                  {!!notificationData?.userNotifications?.nodes?.length && (
                    <span className="absolute top-0 -right-2 inline-block px-2 py-1 text-xs font-bold leading-none text-white bg-yardBackgroundLight rounded-full">
                      {notificationData?.userNotifications?.nodes?.length}
                    </span>
                  )}
                </button>
                {notificationIsOpen && (
                  <div className="absolute right-0 w-[20vw] mt-2 bg-white rounded-lg border shadow-xl z-[12]">
                    <Notification notificationData={notifications} />
                  </div>
                )}
              </div>
            )}
            <div className="relative">
              <button
                onClick={() => setProfileIsOpen(!profileIsOpen)}
                className="relative z-5 block ml-10 border-2 rounded-full p-2"
              >
                <PermIdentityIcon sx={{ fontSize: 20 }} />
              </button>
              {profileIsOpen && (
                <div
                  className="absolute right-0 w-[30vw] xl:w-[25vw] 2xl:w-[20vw] mt-2 bg-white rounded-lg border shadow-xl z-[12]"
                  onMouseLeave={() => {
                    setTimeout(() => {
                      setProfileIsOpen(false);
                    }, 600);
                  }}
                >
                  <UserProfile />
                </div>
              )}
            </div>
          </div>
        </div>
        <Notifier
          notificationMessage={notificationMessage}
          onHideNotifier={onHideNotification}
          onClick={() => {
            handleNotificationPress(
              fcmPayloadData?.data as any as FCMNotificationData,
            );
            onHideNotification();
          }}
        />
      </div>
    </>
  );
};
